import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  doc,
  getDoc,
  deleteDoc,
  writeBatch,
  updateDoc
} from 'firebase/firestore';
import { 
  reauthenticateWithCredential, 
  EmailAuthProvider, 
  updatePassword, 
  deleteUser 
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { FaLock, FaTrashAlt, FaSignOutAlt, FaRunning, FaPlug, FaEdit, FaUsers, FaUserPlus, FaCheck, FaTimes, FaBicycle, FaEye, FaEyeSlash } from 'react-icons/fa';
import { BRAKEACE_STATE_KEY } from './OAuthCallback';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  createGroup,
  getGroupsManagedBy,
  getGroupsWhereMember,
  getGroupByJoinCode,
  requestToJoinGroup,
  getGroupMembers,
  approveMember,
  removeMember,
} from '../utils/userGroups';
import { isAdminUser } from '../utils/admin';
import './Profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [stats, setStats] = useState({
    totalRuns: 0,
    totalDistance: 0,
  });
  
  // Edit username state
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(0);

  // BrakeAce connection state
  const [brakeaceStatus, setBrakeaceStatus] = useState({ connected: false });

  // Strava connection state
  const [connectingStrava, setConnectingStrava] = useState(false);
  const [stravaStatus, setStravaStatus] = useState({
    connected: false,
    athleteName: null,
    lastSyncRangeDays: 90,
    lastSyncAt: null,
  });
  const [stravaSyncing, setStravaSyncing] = useState(false);
  const [stravaRangeDays, setStravaRangeDays] = useState(90);
  const [stravaSyncFeedback, setStravaSyncFeedback] = useState({
    status: 'idle', // idle | syncing | success | error
    message: null,
    data: null,
  });

  // User groups state (manager / member)
  const [groupsManaged, setGroupsManaged] = useState([]);
  const [groupsMember, setGroupsMember] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [joinCodeInput, setJoinCodeInput] = useState('');
  const [joiningGroup, setJoiningGroup] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberActionLoading, setMemberActionLoading] = useState(null);

  useEffect(() => {
    if (!showDeleteModal) return;

    const measure = () => {
      const el = document.querySelector('.sidebar');
      const w = el && el.getBoundingClientRect ? el.getBoundingClientRect().width : 0;
      setSidebarWidth(Number.isFinite(w) ? w : 0);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [showDeleteModal]);

  useEffect(() => {
    if (user) {
      loadUserData();
      loadUserStats();
      loadGroups();
    }
  }, [user]);

  const loadGroups = async () => {
    if (!user) return;
    try {
      const [managed, member] = await Promise.all([
        getGroupsManagedBy(user.uid),
        getGroupsWhereMember(user.uid),
      ]);
      setGroupsManaged(managed);
      setGroupsMember(member);
      if (selectedGroupId && managed.some((g) => g.id === selectedGroupId)) {
        loadGroupMembers(selectedGroupId);
      } else {
        setSelectedGroupId(null);
        setGroupMembers([]);
      }
    } catch (err) {
      console.error('Error loading groups:', err);
    }
  };

  const loadGroupMembers = async (groupId) => {
    if (!user || !groupId) return;
    setLoadingMembers(true);
    try {
      const members = await getGroupMembers(groupId);
      setGroupMembers(members);
    } catch (err) {
      console.error('Error loading group members:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!user || !groupName.trim()) {
      alert('Enter a group name');
      return;
    }
    setCreatingGroup(true);
    try {
      const { id, joinCode } = await createGroup(groupName.trim(), user.uid);
      setGroupName('');
      await loadGroups();
      setSelectedGroupId(id);
      loadGroupMembers(id);
      alert(`Group created. Share this code for others to join: ${joinCode}`);
    } catch (err) {
      console.error('Error creating group:', err);
      alert('Failed to create group: ' + (err?.message || err));
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleJoinGroup = async () => {
    const code = (joinCodeInput || '').trim().toUpperCase();
    if (!user || !code) {
      alert('Enter the join code from your manager');
      return;
    }
    setJoiningGroup(true);
    try {
      const group = await getGroupByJoinCode(code);
      if (!group) {
        alert('No group found with that code');
        setJoiningGroup(false);
        return;
      }
      const result = await requestToJoinGroup(group.id, user.uid, user.email);
      setJoinCodeInput('');
      if (result.already) {
        alert(result.approved ? 'You are already in this group.' : 'You already have a pending request.');
      } else {
        alert('Join request sent. The manager will approve you.');
      }
      await loadGroups();
    } catch (err) {
      console.error('Error joining group:', err);
      alert('Failed to request join: ' + (err?.message || err));
    } finally {
      setJoiningGroup(false);
    }
  };

  const handleApproveMember = async (memberDocId, groupId) => {
    if (!user) return;
    setMemberActionLoading(memberDocId);
    try {
      await approveMember(memberDocId, groupId, user.uid);
      await loadGroupMembers(groupId);
    } catch (err) {
      console.error('Error approving member:', err);
      alert('Failed to approve: ' + (err?.message || err));
    } finally {
      setMemberActionLoading(null);
    }
  };

  const handleRemoveMember = async (memberDocId, groupId) => {
    if (!user || !window.confirm('Remove this member? They will no longer see shared data and you will not see their activities.')) return;
    setMemberActionLoading(memberDocId);
    try {
      await removeMember(memberDocId, groupId, user.uid);
      await loadGroupMembers(groupId);
    } catch (err) {
      console.error('Error removing member:', err);
      alert('Failed to remove: ' + (err?.message || err));
    } finally {
      setMemberActionLoading(null);
    }
  };

  // Handle OAuth callback from Strava (return URL after authorize)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const state = urlParams.get('state');

    if (state?.startsWith('garmin_')) {
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    const handleStravaCallback = async () => {
      if (error) {
        alert(`Strava authorization failed: ${error}`);
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      if (code && user) {
        try {
          const fn = httpsCallable(getFunctions(), 'stravaExchangeToken');
          await fn({ code });
          window.history.replaceState({}, document.title, window.location.pathname);
          await Promise.all([loadUserData(), loadStravaStatus()]);
          alert('Successfully connected to Strava!');
        } catch (err) {
          console.error('Error completing Strava authentication:', err);
          alert('Failed to complete Strava authentication: ' + err.message);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    };

    if (user && code) {
      handleStravaCallback();
    }
  }, [user]);

  const loadStravaStatus = async () => {
    if (!user) return;
    try {
      const tokenDoc = await getDoc(doc(db, 'stravaTokens', user.uid));
      if (!tokenDoc.exists()) {
        setStravaStatus({ connected: false, athleteName: null, lastSyncRangeDays: 90, lastSyncAt: null });
        return;
      }
      const data = tokenDoc.data();
      const athlete = data?.athlete;
      const athleteName =
        [athlete?.firstname, athlete?.lastname].filter(Boolean).join(' ') ||
        athlete?.username ||
        null;
      setStravaStatus({
        connected: !!data?.connected,
        athleteName,
        lastSyncRangeDays: typeof data?.lastSyncRangeDays === 'number' ? data.lastSyncRangeDays : 90,
        lastSyncAt: data?.lastSyncAt || null,
      });
    } catch (error) {
      console.error('Error loading Strava status:', error);
      setStravaStatus({ connected: false, athleteName: null, lastSyncRangeDays: 90, lastSyncAt: null });
    }
  };

  const formatFirestoreDateTime = (value) => {
    if (!value) return null;
    try {
      if (typeof value?.toDate === 'function') {
        return value.toDate().toLocaleString();
      }
      if (typeof value?.seconds === 'number') {
        return new Date(value.seconds * 1000).toLocaleString();
      }
      if (typeof value === 'number') {
        return new Date(value).toLocaleString();
      }
      return new Date(value).toLocaleString();
    } catch {
      return null;
    }
  };

  const loadUserData = async () => {
    if (!user) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
      }
      await Promise.all([loadStravaStatus(), loadBrakeaceStatus()]);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadBrakeaceStatus = async () => {
    if (!user) return;
    try {
      const tokenDoc = await getDoc(doc(db, 'brakeaceTokens', user.uid));
      setBrakeaceStatus({ connected: !!tokenDoc?.exists?.() });
    } catch (error) {
      console.error('Error loading BrakeAce status:', error);
      setBrakeaceStatus({ connected: false });
    }
  };

  const handleConnectBrakeace = () => {
    if (!user) {
      alert('User not authenticated');
      return;
    }
    const state = `brakeace_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    sessionStorage.setItem(BRAKEACE_STATE_KEY, state);
    const redirectUri = `${window.location.origin}/oauth/callback`;
    const scope = 'rides:read rides:export';
    const authUrl = `https://brakeace-production.web.app/portal/oauth/authorize?client_id=victorise-video-tool&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}&scope=${encodeURIComponent(scope)}&portal_name=Victorise`;
    window.location.href = authUrl;
  };

  const handleDisconnectBrakeace = async () => {
    if (!user) return;
    if (!window.confirm('Disconnect BrakeAce? You will not be able to import rides from BrakeAce.')) return;
    try {
      const fn = httpsCallable(getFunctions(), 'brakeaceDisconnect');
      await fn({});
      await loadBrakeaceStatus();
      alert('BrakeAce disconnected.');
    } catch (error) {
      console.error('Error disconnecting BrakeAce:', error);
      alert('Failed to disconnect: ' + (error?.message || String(error)));
    }
  };

  const loadUserStats = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      let runsQuery;
      let runsData = [];
      
      try {
        // Try to fetch with orderBy
        runsQuery = query(
          collection(db, 'runs'),
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        const runsSnapshot = await getDocs(runsQuery);
        runsData = runsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (orderError) {
        // If orderBy fails (missing index), fetch without it
        runsQuery = query(
          collection(db, 'runs'),
          where('userId', '==', user.uid)
        );
        const runsSnapshot = await getDocs(runsQuery);
        runsData = runsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      // Calculate statistics
      const totalRuns = runsData.length;
      const totalDistance = runsData.reduce((sum, run) => sum + (run.distance || 0), 0);

      setStats({
        totalRuns,
        totalDistance: totalDistance / 1000, // Convert to km
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUsername = async () => {
    if (!user || !newUsername.trim()) {
      alert('Please enter a username');
      return;
    }

    if (newUsername.trim().length < 2) {
      alert('Username must be at least 2 characters');
      return;
    }

    try {
      setSavingUsername(true);
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        username: newUsername.trim()
      });
      await loadUserData(); // Reload to update UI
      setEditingUsername(false);
      setNewUsername('');
      alert('Username updated successfully');
    } catch (error) {
      console.error('Error updating username:', error);
      alert('Failed to update username: ' + error.message);
    } finally {
      setSavingUsername(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user || !user.email) {
      alert('User not authenticated');
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      alert('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    try {
      setChangingPassword(true);
      
      // Reauthenticate user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await updatePassword(user, newPassword);
      
      alert('Password changed successfully');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        alert('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        alert('New password is too weak');
      } else {
        alert(error.message || 'Failed to change password');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = () => {
    if (!user || !user.email) {
      alert('User not authenticated');
      return;
    }

    if (!window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone. All your runs and data will be permanently deleted.'
    )) {
      return;
    }

    setShowDeleteModal(true);
  };

  const confirmDeleteAccount = async () => {
    if (!user || !user.email) {
      alert('User not authenticated');
      return;
    }

    if (!deletePassword) {
      alert('Password is required');
      return;
    }

    try {
      setDeletingAccount(true);

      // Reauthenticate user
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);

      // Delete user's runs
      const runsQuery = query(
        collection(db, 'runs'),
        where('userId', '==', user.uid)
      );
      const runsSnapshot = await getDocs(runsQuery);
      
      if (!runsSnapshot.empty) {
        const batch = writeBatch(db);
        runsSnapshot.docs.forEach((docSnap) => {
          batch.delete(docSnap.ref);
        });
        await batch.commit();
      }

      // Delete user document
      await deleteDoc(doc(db, 'users', user.uid));

      // Delete Firebase Auth user
      await deleteUser(user);

      alert('Account deleted successfully');
      setShowDeleteModal(false);
      setDeletePassword('');
      setShowDeletePassword(false);
      logout();
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/wrong-password') {
        alert('Incorrect password');
      } else {
        alert(error.message || 'Failed to delete account');
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleBuyCredits = () => {
    navigate('/buy-credits');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleConnectStrava = () => {
    if (!user) {
      alert('User not authenticated');
      return;
    }

    const clientId = '184324';
    const redirectUri = `${window.location.origin}/profile`;
    const scope = 'read,activity:read_all';
    
    // Construct the OAuth URL
    const authUrl = `https://www.strava.com/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&approval_prompt=auto&scope=${scope}`;
    
    // Redirect to Strava authorization page
    window.location.href = authUrl;
  };

  const handleDisconnectStrava = async () => {
    if (!user) {
      alert('User not authenticated');
      return;
    }

    if (!window.confirm('Are you sure you want to disconnect Strava? You won\'t be able to import runs from Strava.')) {
      return;
    }

    try {
      const fn = httpsCallable(getFunctions(), 'stravaDisconnect');
      await fn({});

      alert('Strava disconnected successfully!');
      await loadStravaStatus();
    } catch (error) {
      console.error('Error disconnecting Strava:', error);
      alert('Failed to disconnect Strava: ' + (error?.message || String(error)));
    }
  };

  const handleSyncStravaActivities = async () => {
    if (!user) {
      alert('User not authenticated');
      return;
    }
    if (!stravaStatus.connected) {
      alert('Strava not connected');
      return;
    }
    try {
      setStravaSyncing(true);
      setStravaSyncFeedback({
        status: 'syncing',
        message: `Syncing Strava activities (last ${stravaRangeDays} days)…`,
        data: null,
      });
      const fn = httpsCallable(getFunctions(), 'stravaSyncActivities');
      const res = await fn({ rangeDays: stravaRangeDays });
      const data = res?.data;
      await loadStravaStatus();
      setStravaSyncFeedback({
        status: 'success',
        message: `Sync complete: imported/updated ${data?.runsUpserted ?? 0} run(s) from ${data?.activitiesFetched ?? 0} activity(ies).`,
        data: data || null,
      });
    } catch (error) {
      console.error('Error syncing Strava activities:', error);
      const e = error;
      const code = e?.code || 'UNKNOWN';
      const message = e?.message || String(error);
      const detailsMsg = e?.details?.message || e?.details?.reason || null;
      const detailsJson = (() => {
        try {
          return e?.details ? JSON.stringify(e.details) : null;
        } catch {
          return null;
        }
      })();
      setStravaSyncFeedback({
        status: 'error',
        message: `Sync failed (${code}): ${detailsMsg || detailsJson || message}`,
        data: null,
      });
    } finally {
      setStravaSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-header" style={{ marginBottom: 24 }}>
          <div className="page-title-with-spinner">
            <h1>Profile</h1>
            <span className="page-title-spinner" aria-hidden="true" />
          </div>
          <p style={{ margin: 0, color: '#888' }}>Loading profile…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* User Info Section */}
      <div className="profile-header">
        <div className="profile-avatar">
          <span className="avatar-text">
            {user?.email?.charAt(0).toUpperCase() || 'U'}
          </span>
        </div>
        <div className="profile-info">
          {editingUsername ? (
            <div className="username-edit-container">
              <input
                type="text"
                className="username-edit-input"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Enter username"
                autoFocus
              />
              <div className="username-edit-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditingUsername(false);
                    setNewUsername('');
                  }}
                  disabled={savingUsername}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveUsername}
                  disabled={savingUsername}
                >
                  {savingUsername ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <div className="username-display-container">
              <h1 className="profile-name">
                {userData?.username || user?.displayName || 'User'}
              </h1>
              <button
                className="edit-username-btn"
                onClick={() => {
                  setEditingUsername(true);
                  setNewUsername(userData?.username || '');
                }}
                title="Edit username"
              >
                <FaEdit />
              </button>
            </div>
          )}
          <p className="profile-email">{user?.email}</p>
          <p className="profile-user-type">Account type: {isAdminUser(user, userData) ? 'Admin' : 'User'}</p>
        </div>
      </div>

      {/* Credits Section */}
      <div className="profile-section">
        <h2 className="section-title">Credits</h2>
        <div className="credits-display">
          <span className="credits-amount">{userData?.credits ?? 0} ⚡</span>
          <button 
            className="btn btn-primary buy-credits-btn"
            onClick={handleBuyCredits}
          >
            Buy Credits
          </button>
        </div>
      </div>

      {/* My Groups Section */}
      <div className="profile-section">
        <h2 className="section-title">
          <FaUsers style={{ marginRight: '8px' }} />
          My Groups
        </h2>
        <p style={{ color: '#888', marginBottom: '16px', fontSize: '14px' }}>
          Create a group to become a manager and invite members. As a manager you can see your members&apos; activities and comment on them. You can also join another group as a member.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              className="form-input"
              placeholder="New group name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              style={{ maxWidth: '220px' }}
            />
            <button
              className="btn btn-primary"
              onClick={handleCreateGroup}
              disabled={creatingGroup || !groupName.trim()}
            >
              {creatingGroup ? 'Creating…' : 'Create group'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Join code (from manager)"
              value={joinCodeInput}
              onChange={(e) => setJoinCodeInput(e.target.value.toUpperCase())}
              style={{ maxWidth: '160px', textTransform: 'uppercase' }}
            />
            <button
              className="btn btn-secondary"
              onClick={handleJoinGroup}
              disabled={joiningGroup || !joinCodeInput.trim()}
            >
              <FaUserPlus style={{ marginRight: '6px' }} />
              {joiningGroup ? 'Joining…' : 'Request to join'}
            </button>
          </div>
        </div>

        {groupsManaged.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ color: '#e0e0e0', fontSize: '15px', marginBottom: '10px' }}>Groups I manage</h3>
            {groupsManaged.map((g) => (
              <div
                key={g.id}
                style={{
                  border: '1px solid #2a2a2a',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '8px',
                  background: selectedGroupId === g.id ? '#1a1a1a' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <strong style={{ color: '#e0e0e0' }}>{g.name}</strong>
                    <span style={{ color: '#666', marginLeft: '10px', fontSize: '13px' }}>
                      Code: <code style={{ background: '#2a2a2a', padding: '2px 6px', borderRadius: '4px' }}>{g.joinCode}</code>
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: '13px' }}
                    onClick={() => {
                      setSelectedGroupId(selectedGroupId === g.id ? null : g.id);
                      if (selectedGroupId !== g.id) loadGroupMembers(g.id);
                      else setGroupMembers([]);
                    }}
                  >
                    {selectedGroupId === g.id ? 'Hide members' : 'Show members'}
                  </button>
                </div>
                {selectedGroupId === g.id && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #2a2a2a' }}>
                    {loadingMembers ? (
                      <div style={{ color: '#888' }}>Loading members…</div>
                    ) : groupMembers.length === 0 ? (
                      <div style={{ color: '#888' }}>No members yet. Share the join code.</div>
                    ) : (
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {groupMembers.map((m) => (
                          <li
                            key={m.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '6px 0',
                              borderBottom: '1px solid #222',
                            }}
                          >
                            <span style={{ color: '#ccc' }}>
                              {m.username || m.email || m.userId}
                              {m.status === 'pending' && (
                                <span style={{ color: '#f0ad4e', marginLeft: '8px', fontSize: '12px' }}>Pending</span>
                              )}
                            </span>
                            <span>
                              {m.status === 'pending' ? (
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  style={{ fontSize: '12px', padding: '4px 10px' }}
                                  disabled={memberActionLoading === m.id}
                                  onClick={() => handleApproveMember(m.id, g.id)}
                                >
                                  {memberActionLoading === m.id ? '…' : <><FaCheck /> Approve</>}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="btn btn-secondary"
                                style={{ fontSize: '12px', padding: '4px 10px', marginLeft: '6px' }}
                                disabled={memberActionLoading === m.id}
                                onClick={() => handleRemoveMember(m.id, g.id)}
                                title="Remove member"
                              >
                                {memberActionLoading === m.id ? '…' : <FaTimes />}
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {groupsMember.length > 0 && (
          <div>
            <h3 style={{ color: '#e0e0e0', fontSize: '15px', marginBottom: '10px' }}>Groups I belong to (member)</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {groupsMember.map((g) => (
                <li
                  key={g.id}
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid #2a2a2a',
                    color: '#ccc',
                  }}
                >
                  <strong style={{ color: '#e0e0e0' }}>{g.name}</strong>
                  <span style={{ color: '#888', marginLeft: '8px', fontSize: '13px' }}>Member</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {groupsManaged.length === 0 && groupsMember.length === 0 && (
          <p style={{ color: '#666', fontSize: '14px' }}>You have no groups yet. Create one or request to join with a code.</p>
        )}
      </div>

      {/* Strava Connection Section */}
      <div className="profile-section">
        <h2 className="section-title">Strava Integration</h2>
        {stravaStatus.connected ? (
          <div className="strava-connected">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <FaRunning style={{ color: '#FC4C02', fontSize: '24px' }} />
              <span style={{ color: '#e0e0e0', fontSize: '16px', fontWeight: '600' }}>
                Connected to Strava{stravaStatus.athleteName ? ` as ${stravaStatus.athleteName}` : ''}
              </span>
            </div>
            <p style={{ color: '#888', marginBottom: '16px', fontSize: '14px' }}>
              You can now import your Strava runs and activities
            </p>
            {stravaStatus.lastSyncAt && (
              <p style={{ color: '#888', marginBottom: '16px', fontSize: '13px' }}>
                Last sync: {formatFirestoreDateTime(stravaStatus.lastSyncAt) || '—'}
              </p>
            )}

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
              <label style={{ color: '#888', fontSize: '14px' }}>
                Range:
                <select
                  value={stravaRangeDays}
                  onChange={(e) => setStravaRangeDays(Number(e.target.value))}
                  disabled={stravaSyncing}
                  style={{ marginLeft: '8px' }}
                >
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={180}>Last 180 days</option>
                  <option value={365}>Last 365 days</option>
                </select>
              </label>

              <button
                className="btn btn-primary"
                onClick={handleSyncStravaActivities}
                disabled={stravaSyncing}
                style={{ width: 'auto', minWidth: '200px' }}
              >
                {stravaSyncing ? 'Syncing…' : 'Sync activities'}
              </button>
            </div>

            {stravaSyncFeedback?.status !== 'idle' && (
              <div
                style={{
                  marginBottom: 16,
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #2a2a2a',
                  background: '#101010',
                }}
              >
                <div style={{ color: stravaSyncFeedback.status === 'error' ? '#dc3545' : '#e0e0e0', fontSize: 14, fontWeight: 600 }}>
                  {stravaSyncFeedback.status === 'syncing'
                    ? 'Sync in progress'
                    : stravaSyncFeedback.status === 'success'
                      ? 'Sync complete'
                      : 'Sync failed'}
                </div>
                {stravaSyncFeedback.message && (
                  <div style={{ color: '#888', marginTop: 6, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    {stravaSyncFeedback.message}
                  </div>
                )}

                {stravaSyncFeedback.status === 'success' && stravaSyncFeedback.data && (
                  <div style={{ color: '#888', marginTop: 10, fontSize: 13 }}>
                    Found: {stravaSyncFeedback.data.activitiesFetched ?? 0} activities • Imported/updated:{' '}
                    {stravaSyncFeedback.data.runsUpserted ?? 0} runs • Streams fetched:{' '}
                    {stravaSyncFeedback.data.streamsFetched ?? 0}
                  </div>
                )}

                {stravaSyncFeedback.status === 'success' && (
                  <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate('/my-rides')}
                      style={{ width: 'auto', minWidth: '200px' }}
                    >
                      Next: View runs
                    </button>
                  </div>
                )}
              </div>
            )}

            <button 
              className="btn btn-primary"
              onClick={handleDisconnectStrava}
              style={{ width: 'auto', minWidth: '200px' }}
            >
              Disconnect Strava
            </button>
          </div>
        ) : (
          <div className="strava-not-connected">
            <p style={{ color: '#888', marginBottom: '16px' }}>
              Connect your Strava account to import runs and activities
            </p>
            <button 
              className="menu-item"
              onClick={handleConnectStrava}
            >
              <FaRunning className="menu-item-icon" style={{ color: '#FC4C02' }} />
              Connect to Strava
            </button>
          </div>
        )}
      </div>

      {/* BrakeAce Connection Section */}
      <div className="profile-section">
        <h2 className="section-title">BrakeAce Integration</h2>
        {brakeaceStatus.connected ? (
          <div className="strava-connected">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <FaBicycle style={{ color: '#00d4aa', fontSize: '24px' }} />
              <span style={{ color: '#e0e0e0', fontSize: '16px', fontWeight: '600' }}>
                Connected to BrakeAce
              </span>
            </div>
            <p style={{ color: '#888', marginBottom: '16px', fontSize: '14px' }}>
              You can import BrakeAce rides when creating a new run
            </p>
            <button
              className="btn btn-secondary"
              onClick={handleDisconnectBrakeace}
              style={{ width: 'auto', minWidth: '200px' }}
            >
              Disconnect BrakeAce
            </button>
          </div>
        ) : (
          <div className="strava-not-connected">
            <p style={{ color: '#888', marginBottom: '16px' }}>
              Connect your BrakeAce account to import rides with telemetry data
            </p>
            <button
              className="menu-item"
              onClick={handleConnectBrakeace}
            >
              <FaBicycle className="menu-item-icon" style={{ color: '#00d4aa' }} />
              Connect to BrakeAce
            </button>
          </div>
        )}
      </div>

      {/* Settings Menu */}
      <div className="profile-section">
        <h2 className="section-title">Settings</h2>
        <div className="profile-menu">
          {!showChangePassword ? (
            <button 
              className="menu-item"
              onClick={() => setShowChangePassword(true)}
            >
              <FaLock className="menu-item-icon" />
              Change Password
            </button>
          ) : (
            <div className="password-form-section">
              <div className="password-form">
                <div className="password-field-wrapper">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="form-input password-form-input"
                    placeholder="Current Password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="password-visibility-btn"
                    onClick={() => setShowCurrentPassword((prev) => !prev)}
                    aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                    title={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                  >
                    {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <div className="password-field-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className="form-input password-form-input"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="password-visibility-btn"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                    aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                    title={showNewPassword ? 'Hide new password' : 'Show new password'}
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <div className="password-field-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="form-input password-form-input"
                    placeholder="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="password-visibility-btn"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                    title={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <div className="form-actions">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowChangePassword(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setShowCurrentPassword(false);
                      setShowNewPassword(false);
                      setShowConfirmPassword(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                  >
                    {changingPassword ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}
          <button 
            className="menu-item delete-account-btn"
            onClick={handleDeleteAccount}
          >
            <FaTrashAlt className="menu-item-icon" />
            Delete Account
          </button>
          <button 
            className="menu-item logout-btn"
            onClick={handleLogout}
          >
            <FaSignOutAlt className="menu-item-icon" />
            Logout
          </button>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div 
          className="modal-overlay" 
          style={{
            left: sidebarWidth ? `${sidebarWidth}px` : 0,
            width: sidebarWidth ? `calc(100% - ${sidebarWidth}px)` : '100%',
          }}
          onClick={() => {
            setShowDeleteModal(false);
            setDeletePassword('');
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Confirm Account Deletion</h2>
            <p className="modal-text">
              Enter your password to confirm account deletion. This action cannot be undone.
            </p>
            <div className="password-field-wrapper modal-password-wrapper">
              <input
                type={showDeletePassword ? 'text' : 'password'}
                className="form-input modal-input password-form-input"
                placeholder="Enter your password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                autoFocus
              />
              <button
                type="button"
                className="password-visibility-btn"
                onClick={() => setShowDeletePassword((prev) => !prev)}
                aria-label={showDeletePassword ? 'Hide password' : 'Show password'}
                title={showDeletePassword ? 'Hide password' : 'Show password'}
              >
                {showDeletePassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setShowDeletePassword(false);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmDeleteAccount}
                disabled={deletingAccount}
              >
                {deletingAccount ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;

